import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Badge, EmptyState, SearchLg, Tabs, TextInput, XClose } from '@opendate-app/setlist';
import client from '../../../../shared/api/client';
import { EVENT_TABS } from '../../../EventPage/constants/tabs';
import './universalSearch.css';

export const inputIdFor = (layout) => `universal-search-input-${layout}`;
const DEBOUNCE_MS = 250;
const EMPTY = { events: { upcoming: [], past: [] }, artists: [], offers: [], fans: [] };
const DEFAULT_SECTIONS = [
  { key: 'events', label: 'Events' },
  { key: 'artists', label: 'Artists' },
  { key: 'offers', label: 'Offers' },
  { key: 'fans', label: 'Fans' },
];
const listPhrase = (words) => (words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words.join(''));
const OFFER_STATE_COLORS = { draft: 'gray', sent: 'blue', accepted: 'success', declined: 'error' };
const CLASSIFICATION_COLORS = {
  on_sale: 'success',
  pre_sale: 'blue',
  artist_pre_sale: 'blue',
  announced: 'blue',
  confirm: 'indigo',
  hold: 'warning',
  postponed: 'warning',
  canceled: 'error',
  past: 'gray',
};
const isEditable = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
const EVENT_PATH = /\/teams\/\d+\/(?:e|confirms)\/(\d+)/;
const DAY_MS = 86400000;

const initials = (text) => (
  (text || '').replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '—'
);

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlight = ({ text, term }) => {
  const parts = term.trim().split(/\s+/).filter((t) => t.length > 1).map(escapeRegExp);
  if (!text || parts.length === 0) return <>{text}</>;
  const matcher = new RegExp(`^(${parts.join('|')})$`, 'i');
  return (
    <>
      {text.split(new RegExp(`(${parts.join('|')})`, 'ig')).map((piece, i) => (
        matcher.test(piece) ? <mark key={i}>{piece}</mark> : <React.Fragment key={i}>{piece}</React.Fragment>
      ))}
    </>
  );
};

Highlight.propTypes = { text: PropTypes.string, term: PropTypes.string.isRequired };

const parseDate = (iso) => (iso ? new Date(`${iso}T12:00:00`) : null);

const relativeDay = (iso) => {
  const date = parseDate(iso);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.floor((date - today) / DAY_MS);
  if (days === 0) return 'Tonight';
  if (days === 1) return 'Tomorrow';
  if (days > 1 && days <= 7) return `In ${days} days`;
  return null;
};

const formatDate = (iso) => {
  const date = parseDate(iso);
  if (!date) return null;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
};

const readLocation = () => {
  const match = window.location.pathname.match(EVENT_PATH);
  const hashKey = window.location.hash.replace('#', '');
  const tab = match ? EVENT_TABS.find((t) => t.key === hashKey) : null;
  return {
    eventId: match ? Number(match[1]) : null,
    tabHash: tab ? `#${tab.key}` : '',
    tabLabel: tab && tab.key !== EVENT_TABS[0].key ? tab.label : null,
  };
};

const focusInputIn = (container, { select = false } = {}) => {
  const input = container && container.querySelector('input');
  if (!input) return;
  input.focus();
  if (select) input.select();
};
const isMacLike = () => /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');

const UniversalSearch = ({ searchUrl, sections, layout, collapsed, onExpand }) => {
  const [query, setQuery] = useState('');
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('all');
  const [active, setActive] = useState(0);
  const [here, setHere] = useState({ eventId: null, tabHash: '', tabLabel: null });
  const [mounted, setMounted] = useState(false);
  const [fetchedTerm, setFetchedTerm] = useState('');
  const [failed, setFailed] = useState(false);
  const [shortcutLabel, setShortcutLabel] = useState('⌘K');
  const viaKeyboard = useRef(false);
  const containerRef = useRef(null);
  const focusWhenExpanded = useRef(false);
  const term = query.trim();
  const inputId = inputIdFor(layout);
  const listboxId = `${inputId}-listbox`;
  const focusInput = (options) => focusInputIn(containerRef.current, options);

  useEffect(() => {
    setMounted(true);
    if (!isMacLike()) setShortcutLabel('Ctrl K');
  }, []);

  useEffect(() => {
    const sync = () => setHere(readLocation());
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  useEffect(() => {
    setFailed(false);
    if (!term) {
      setData(EMPTY);
      setFetchedTerm('');
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let live = true;
    const timer = setTimeout(() => {
      client.get(searchUrl, { params: { q: term } })
        .then(({ data: response }) => {
          if (!live) return;
          if (!response || !response.events || !response.artists || !response.offers || !response.fans) throw new Error('unexpected response');
          setData(response);
          setFailed(false);
          setFetchedTerm(term);
          setLoading(false);
        })
        .catch(() => { if (live) { setData(EMPTY); setFailed(true); setLoading(false); } });
    }, DEBOUNCE_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [term, searchUrl]);

  useEffect(() => { setActive(0); }, [data, scope]);

  useEffect(() => {
    if (!viaKeyboard.current) return;
    viaKeyboard.current = false;
    const row = containerRef.current && containerRef.current.querySelector('[data-active="true"]');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setScope('all');
  }, []);

  const openFromShortcut = useCallback(() => {
    if (collapsed) {
      focusWhenExpanded.current = true;
      onExpand();
      return;
    }
    setOpen(true);
    focusInput({ select: true });
  }, [collapsed, onExpand]);

  const openRef = useRef(open);
  const openFromShortcutRef = useRef(openFromShortcut);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { openFromShortcutRef.current = openFromShortcut; }, [openFromShortcut]);

  useEffect(() => {
    const onKey = (e) => {
      if (layout !== 'sidebar') return;
      if (e.defaultPrevented) return;
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k') return;
      if (isEditable(e.target) && !(containerRef.current && containerRef.current.contains(e.target))) return;
      e.preventDefault();
      if (openRef.current) close(); else openFromShortcutRef.current();
    };
    const onMouseDown = (e) => {
      if (openRef.current && containerRef.current && !containerRef.current.contains(e.target)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [close, layout]);

  useEffect(() => {
    if (collapsed || !focusWhenExpanded.current) return;
    focusWhenExpanded.current = false;
    setOpen(true);
    focusInput({ select: true });
  }, [collapsed]);

  if (collapsed) {
    return (
      <div className="nav-v2-search nav-v2-search--rail">
        <button type="button" className="universal-search-rail" aria-label="Search" title="Search" onClick={openFromShortcut}>
          <SearchLg width={20} height={20} />
        </button>
      </div>
    );
  }

  if (!mounted) {
    return <div className={'nav-v2-search universal-search universal-search--' + layout + ' universal-search--pending'} aria-hidden="true" />;
  }

  const labels = Object.fromEntries(sections.map((s) => [s.key, s.label]));
  const scopes = [{ key: 'all', label: 'All' }, ...sections];
  const searchesFor = listPhrase(sections.map((s) => s.label.toLowerCase()));
  const shows = (key) => !!labels[key] && (scope === 'all' || scope === key);
  const upcoming = shows('events') ? data.events.upcoming : [];
  const past = shows('events') ? data.events.past : [];
  const artists = shows('artists') ? data.artists : [];
  const offers = shows('offers') ? data.offers : [];
  const fans = shows('fans') ? data.fans : [];
  const rows = [
    ...upcoming.map((item) => ({ kind: 'event', item })),
    ...past.map((item) => ({ kind: 'event', item })),
    ...artists.map((item) => ({ kind: 'artist', item })),
    ...offers.map((item) => ({ kind: 'offer', item })),
    ...fans.map((item) => ({ kind: 'fan', item })),
  ];
  const isHere = (row) => row.kind === 'event' && row.item.id === here.eventId;

  const hrefFor = (row) => (row.kind === 'event' ? row.item.href + here.tabHash : row.item.href);

  const go = (row) => {
    if (isHere(row)) return;
    window.location.href = hrefFor(row);
  };

  const stale = loading || fetchedTerm !== term;

  const onContainerKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    focusInput();
    close();
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); viaKeyboard.current = true; setActive((i) => Math.min(i + 1, Math.max(rows.length - 1, 0))); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); viaKeyboard.current = true; setActive((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); if (!stale && rows[active]) go(rows[active]); }
  };

  const onContainerBlur = (e) => {
    if (open && containerRef.current && !containerRef.current.contains(e.relatedTarget)) close();
  };

  let cursor = -1;
  const renderRow = (row) => {
    cursor += 1;
    const index = cursor;
    const isActive = index === active;
    const current = isHere(row);
    return (
      <a key={row.kind === 'offer' ? row.item.href : `${row.kind}-${row.item.id}`}
         id={`${inputId}-option-${index}`}
         href={current ? undefined : hrefFor(row)}
         role="option"
         tabIndex={-1}
         aria-selected={isActive}
         aria-disabled={current || undefined}
         data-active={isActive ? 'true' : undefined}
         className={'universal-search-row' + (isActive ? ' is-active' : '') + (current ? ' is-here' : '')}
         onMouseEnter={() => setActive(index)}
         onMouseDown={(e) => e.preventDefault()}
         onClick={(e) => { if (current) e.preventDefault(); }}>
        {row.kind === 'event' && renderEvent(row.item, current)}
        {row.kind === 'artist' && renderArtist(row.item)}
        {row.kind === 'offer' && renderOffer(row.item)}
        {row.kind === 'fan' && renderFan(row.item)}
      </a>
    );
  };

  const renderEvent = (event, current) => {
    const badge = { color: CLASSIFICATION_COLORS[event.classification] || 'gray', label: event.classification_label || event.classification };
    const relative = relativeDay(event.start_date);
    const subtitle = [formatDate(event.start_date), event.venue_name].filter(Boolean).join(' · ');
    return (
      <>
        <Avatar size="sm" initials={initials(event.title)} alt="" />
        <span className="universal-search-main">
          <span className="universal-search-title"><Highlight text={event.title} term={term} /></span>
          <span className="universal-search-sub">
            {relative && <b>{relative}</b>}
            {relative && subtitle && ' · '}
            {subtitle}
          </span>
          <span className="universal-search-meta">
            {current
              ? <Badge type="pill-color" color="purple" size="sm">You are here</Badge>
              : <Badge type="pill-color" color={badge.color} size="sm" dot>{badge.label}</Badge>}
          </span>
        </span>
      </>
    );
  };

  const renderArtist = (artist) => (
    <>
      <Avatar size="sm" src={artist.image_url || undefined} initials={initials(artist.name)} alt="" />
      <span className="universal-search-main">
        <span className="universal-search-title"><Highlight text={artist.name} term={term} /></span>
        <span className="universal-search-sub">
          {artist.upcoming_count > 0
            ? `${artist.upcoming_count} upcoming ${artist.upcoming_count === 1 ? 'show' : 'shows'}`
            : 'No upcoming shows'}
        </span>
      </span>
    </>
  );

  const renderOffer = (offer) => {
    const subtitle = [offer.event_name !== offer.title ? offer.event_name : null, formatDate(offer.start_date), offer.venue_name].filter(Boolean).join(' · ');
    return (
      <>
        <Avatar size="sm" initials={initials(offer.title)} alt="" />
        <span className="universal-search-main">
          <span className="universal-search-title"><Highlight text={offer.title} term={term} /></span>
          {subtitle && <span className="universal-search-sub"><Highlight text={subtitle} term={term} /></span>}
          <span className="universal-search-meta">
            <Badge type="pill-color" color={OFFER_STATE_COLORS[offer.state] || 'gray'} size="sm" dot>{offer.state_label || offer.state}</Badge>
          </span>
        </span>
      </>
    );
  };

  const renderFan = (fan) => (
    <>
      <Avatar size="sm" src={fan.image_url || undefined} initials={initials(fan.name || fan.email)} alt="" />
      <span className="universal-search-main">
        <span className="universal-search-title"><Highlight text={fan.name || fan.email} term={term} /></span>
        {(fan.name || fan.order_count > 0) && (
          <span className="universal-search-sub">
            {fan.name && <Highlight text={fan.email} term={term} />}
            {fan.name && fan.order_count > 0 && ' · '}
            {fan.order_count > 0 && `${fan.order_count} ${fan.order_count === 1 ? 'order' : 'orders'}`}
          </span>
        )}
      </span>
    </>
  );

  const clearButton = (
    <button type="button" className="universal-search-clear" aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setQuery(''); focusInput(); }}>
      <XClose width={16} height={16} />
    </button>
  );

  return (
    <div className={'nav-v2-search universal-search universal-search--' + layout} ref={containerRef}
         onBlur={onContainerBlur} onKeyDown={onContainerKeyDown}>
      <TextInput id={inputId}
                 size="sm"
                 type="text"
                 autoComplete="off"
                 spellCheck={false}
                 placeholder="Search"
                 aria-label={`Search ${searchesFor}`}
                 role="combobox"
                 aria-expanded={open}
                 aria-controls={listboxId}
                 aria-autocomplete="list"
                 aria-activedescendant={open && rows[active] ? `${inputId}-option-${active}` : undefined}
                 leadingIcon={SearchLg}
                 trailingText={query ? clearButton : (layout === 'sidebar' ? <kbd className="universal-search-kbd">{shortcutLabel}</kbd> : undefined)}
                 value={query}
                 onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                 maxLength={100}
                 onFocus={() => setOpen(true)}
                 onClick={() => setOpen(true)}
                 onKeyDown={onKeyDown} />
      {open && (
        <div className="universal-search-dropdown" onMouseDown={(e) => e.preventDefault()}>
          <div className="universal-search-scopes">
            <Tabs size="sm"
                  variant="button-gray"
                  collapseBelow={0}
                  value={scope}
                  onChange={(value) => { setScope(value); focusInput(); }}
                  items={scopes.map((s) => ({ value: s.key, label: s.label }))} />
          </div>
          {here.tabLabel && (
            <div className="universal-search-hint">Stays on <b>{here.tabLabel}</b></div>
          )}
          <div className="universal-search-body">
            {!term && (
              <EmptyState size="sm" icon={SearchLg} showRings={false} title="Start typing"
                          description={`Search ${searchesFor}.`} />
            )}
            {term && failed && <div className="universal-search-failed" role="alert">Search failed. Keep typing to try again.</div>}
            {term && !failed && stale && rows.length === 0 && <div className="universal-search-loading">Searching…</div>}
            {term && !failed && !stale && rows.length === 0 && (
              <EmptyState size="sm" icon={SearchLg} showRings={false} title="No results"
                          description={`Nothing matches “${term}”.`} />
            )}
            <div id={listboxId} role="listbox" aria-label="Search results">
              {upcoming.length + past.length > 0 && (
                <div role="group" aria-label={labels.events}>
                  <div className="universal-search-group" role="presentation">{labels.events}</div>
                  {upcoming.map((item) => renderRow({ kind: 'event', item }))}
                  {past.length > 0 && (
                    <div className="universal-search-rule" role="presentation"><span /><span>Past</span><span /></div>
                  )}
                  {past.map((item) => renderRow({ kind: 'event', item }))}
                </div>
              )}
              {artists.length > 0 && (
                <div role="group" aria-label={labels.artists}>
                  <div className="universal-search-group" role="presentation">{labels.artists}</div>
                  {artists.map((item) => renderRow({ kind: 'artist', item }))}
                </div>
              )}
              {offers.length > 0 && (
                <div role="group" aria-label={labels.offers}>
                  <div className="universal-search-group" role="presentation">{labels.offers}</div>
                  {offers.map((item) => renderRow({ kind: 'offer', item }))}
                </div>
              )}
              {fans.length > 0 && (
                <div role="group" aria-label={labels.fans}>
                  <div className="universal-search-group" role="presentation">{labels.fans}</div>
                  {fans.map((item) => renderRow({ kind: 'fan', item }))}
                </div>
              )}
            </div>
          </div>
          <div className="universal-search-foot">
            <span><kbd>↑</kbd><kbd>↓</kbd></span>
            <span><kbd>↵</kbd> open</span>
            <span><kbd>esc</kbd></span>
          </div>
        </div>
      )}
    </div>
  );
};

UniversalSearch.propTypes = {
  searchUrl: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })),
  layout: PropTypes.oneOf(['sidebar', 'drawer']),
  collapsed: PropTypes.bool,
  onExpand: PropTypes.func,
};

UniversalSearch.defaultProps = {
  sections: DEFAULT_SECTIONS,
  layout: 'sidebar',
  collapsed: false,
  onExpand: () => {},
};

export default UniversalSearch;
